import { useNavigate } from "react-router-dom";
import { Clock, AlertTriangle, CheckCircle2, XCircle, HelpCircle, ChevronRight } from "lucide-react";
import { StatusBadge } from "@/components/ui-helpers";

/**
 * Format a pretty role label — "manager" → "Manager", "approver_l2" → "Approver L2".
 */
function titleCaseRole(r) {
  if (!r) return "";
  return r
    .replace(/[_-]+/g, " ")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

/**
 * Status pill for a voucher list row.
 *
 *  - `status`         : the voucher's own status field ("Pending", "Approved", …).
 *  - `pendingInfo`    : (optional) result for this row from `useApprovalPendingMap`.
 *
 * When the voucher is Pending and we have approval metadata, this renders a
 * richer "Pending at L{n} — {Role} · {Ndays}" pill, color-coded by aging:
 *
 *      0 day      → amber  (waiting today)
 *      1-2 days   → orange (waiting a few days)
 *     ≥ 3 days    → red    (stale / blocked)
 *
 * Falls back to the global `StatusBadge` for any non-Pending status, or when
 * no approval request is available (legacy docs, matrix-less modules, …).
 */
export function PendingApprovalBadge({ status, pendingInfo }) {
  // Whenever the backend reports an active approval request for this row,
  // surface the rich badge — regardless of the source doc's own status field.
  // (Several voucher modules — estimates / purchase- & sales-invoices /
  // purchase-orders / sales-notes — keep doc.status='Draft' while
  // approval_request.status is 'Pending'. Trust the approval map.)
  if (pendingInfo && (pendingInfo.status === "Query Raised" || pendingInfo.status === "Pending")) {
    if (pendingInfo.status === "Query Raised") {
      const level = pendingInfo.current_level;
      const final = pendingInfo.final_level;
      return (
        <span
          title={`Query raised at L${level}/L${final}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border bg-violet-50 text-violet-800 border-violet-200 whitespace-nowrap"
        >
          <HelpCircle size={10} /> Query · L{level}/L{final}
        </span>
      );
    }
    // Pending — drop into the rich-badge render below.
  } else {
    // No pending request for this row → fall back to the global badge.
    return <StatusBadge status={status} />;
  }

  const { current_level: level, final_level: final, role_at_level: role, days_pending: days } = pendingInfo;
  const roleLabel = titleCaseRole(role) || `L${level} Approvers`;
  let cls = "bg-amber-50 text-amber-800 border-amber-200";
  let Icon = Clock;
  if (days >= 3) {
    cls = "bg-red-50 text-red-700 border-red-200";
    Icon = AlertTriangle;
  } else if (days >= 1) {
    cls = "bg-orange-50 text-orange-800 border-orange-200";
    Icon = Clock;
  }
  const sinceLabel = days <= 0 ? "today" : `${days}d`;
  return (
    <span
      title={`Pending at level ${level} of ${final} · waiting ${sinceLabel} · approver role: ${roleLabel}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap ${cls}`}
      data-testid="pending-approval-badge"
    >
      <Icon size={10} /> L{level}/L{final} · {roleLabel} · {sinceLabel}
    </span>
  );
}

/**
 * Compact "Take Action" pill rendered in the Actions column of voucher list
 * pages. Visible only when:
 *   - the row is Pending,
 *   - we have approval metadata (request id),
 *   - the user is in fact empowered to act at the current level (`can_act`).
 *
 * Clicking deep-links to the Approvals inbox with the request id, so the user
 * can review the document and Approve / Reject / Raise Query in one screen.
 */
export function TakeActionButton({ pendingInfo, size = "sm", testid }) {
  const navigate = useNavigate();
  if (!pendingInfo || !pendingInfo.request_id) return null;
  if (!pendingInfo.can_act) return null;
  const goto = () => navigate(`/approvals?req=${pendingInfo.request_id}`);
  const cls =
    size === "xs"
      ? "text-[9px] px-1.5 py-0.5"
      : "text-[10px] px-2 py-0.5";
  return (
    <button
      type="button"
      onClick={goto}
      data-testid={testid || `take-action-${pendingInfo.request_id}`}
      className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-600 hover:text-white transition-colors ${cls}`}
      title={`Approve / Reject at L${pendingInfo.current_level}`}
    >
      <CheckCircle2 size={11} /> Take Action <ChevronRight size={10} />
    </button>
  );
}

/** Helper: derive the list of likely-pending document IDs from a voucher list.
 *  Many ERP modules keep doc.status='Draft' while the approval workflow is in
 *  flight, so we include both 'Draft' and 'Pending' here — the backend simply
 *  omits ids without an open approval_request, so feeding extra is safe.
 */
export function pendingDocIds(list, statusKey = "status") {
  return (list || [])
    .filter((v) => v && (v[statusKey] === "Pending" || v[statusKey] === "Draft" || v[statusKey] === "Query Raised"))
    .map((v) => v.id)
    .filter(Boolean);
}

// Re-export for convenience (so callers can grab everything from one file).
export { StatusBadge, XCircle };
